import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import {
    type GradingFields,
    type IdVerificationGate,
    type ProjectFieldsModalProps,
    type ScoreOptionValue,
    type UseProjectFieldsFormReturn,
} from './ProjectFieldsModal.types';
import {
    applyAllowResubmissionDueDate,
    applyApproval,
    applyAutoFlagForAdminReview,
    gradesProvisionally,
    hasProvisionalScoreSet,
    idVerificationGate,
    recalculateAllowResubmission,
    setProvisionalScoreOptionValue,
    setScoreOptionValue,
    shouldForceApproval,
} from './projectFieldsLogic';
import { notifyToast } from './notifyToast';
import { saveProjectProgress } from './saveProjectProgress';

const GRADING_FIELD_KEYS: (keyof GradingFields)[] = [
    'score',
    'provisionalScore',
    'waived',
    'resubmissionRequired',
    'allowResubmission',
    'status',
    'feedback',
    'provisionalFeedback',
    'idVerified',
    'resubmissionDueDate',
    'projectSubmissionSimilarityAndOriginalityStatus',
    'projectSubmissionNeedsAdminReview',
    'projectSubmissionPrivateNotes',
    'projectSubmissionSelectedProjectVariant',
    'projectSubmissionPresentationTranscript',
];

// The field changes that re-run the allow-resubmission auto-recalc, as the Angular $watchGroup did.
// The Allow Resubmission checkbox is absent on purpose. A recalc must not override a manual toggle.
const RECALC_FIELDS = new Set<keyof GradingFields>(['status', 'projectSubmissionSimilarityAndOriginalityStatus']);

type State = {
    fields: GradingFields;
    original: GradingFields;
};

type ReducerContext = {
    passingScore: number;
    similarityCheckRequired: boolean;
    historicalSubmissionsCount: number;
    isPresentationProject: boolean;
    // True when the current user is a global admin. Only that user can auto-uncheck a
    // baseline-flagged "needs admin review" on a resubmission handback (#17169).
    isAdmin: boolean;
    hasSubmission: boolean;
};

type Action =
    | { type: 'SCORE_OPTION'; value: ScoreOptionValue }
    | { type: 'FIELD'; key: keyof GradingFields; value: GradingFields[keyof GradingFields] }
    | { type: 'ALLOW_RESUBMISSION'; value: boolean }
    | { type: 'AUTOGRADE'; score: number; feedback: string | null; presentationTranscript: string | null }
    | { type: 'CLEAR_PROVISIONAL_GRADE' }
    | { type: 'RESET'; fields: GradingFields };

function computeHasChanges(state: State): boolean {
    return GRADING_FIELD_KEYS.some(key => state.fields[key] !== state.original[key]);
}

// Re-runs the resubmission auto-recalc and the due-date step after a grading change. `approve` marks
// a Save-and-Approve action. It is true only at save time, through usePrepareSave. The original drove
// it from the approve-checkbox watch. An active resubmission_required also forces approval.
function recompute(state: State, ctx: ReducerContext, approve = false): State {
    const effectiveApprove = approve || shouldForceApproval(state.fields);

    // Approval of a provisional score counts as a change, even with no other edit. The resubmission
    // recalc then runs at save time, as the original's approve-driven hasChanges did.
    const hasChanges = computeHasChanges(state) || (effectiveApprove && hasProvisionalScoreSet(state.fields));

    let fields = recalculateAllowResubmission(state.fields, {
        passingScore: ctx.passingScore,
        approve: effectiveApprove,
        similarityCheckRequired: ctx.similarityCheckRequired,
        historicalSubmissionsCount: ctx.historicalSubmissionsCount,
        hasChanges,
    });
    // The old $watch fired only when allow_resubmission changed. Run the due-date step on that
    // transition alone. An unconditional call overwrites a due date the grader cleared by hand.
    if (fields.allowResubmission === state.fields.allowResubmission) return { ...state, fields };
    return {
        ...state,
        fields: applyAllowResubmissionDueDate(
            fields,
            state.original.resubmissionDueDate,
            Math.floor(Date.now() / 1000),
        ),
    };
}

// Builds the next state for an AUTOGRADE action, before the resubmission recompute. The action
// carries a provisional score and feedback, and sometimes a fresh presentation transcript.
//
// The transcript refreshes only when the grader did not edit it, so the current value still matches
// the saved baseline. That protects an unsaved edit. The baseline moves too, so the new value does
// not look dirty.
function applyAutograde(state: State, action: Extract<Action, { type: 'AUTOGRADE' }>): State {
    const working: State = {
        ...state,
        fields: {
            ...setProvisionalScoreOptionValue(state.fields, action.score),
            provisionalFeedback: action.feedback,
        },
    };
    const transcriptNotDirty =
        state.fields.projectSubmissionPresentationTranscript === state.original.projectSubmissionPresentationTranscript;
    if (action.presentationTranscript == null || !transcriptNotDirty) return working;
    return {
        ...working,
        fields: { ...working.fields, projectSubmissionPresentationTranscript: action.presentationTranscript },
        original: { ...working.original, projectSubmissionPresentationTranscript: action.presentationTranscript },
    };
}

// #17169: on a resubmission handback, auto-uncheck "needs admin review", so the grader does not have
// to. A handback means an unverified ID plus resubmission_required. This runs only when
// resubmission_required or id_verified changes, never on the checkbox itself. An admin who checks the
// box again therefore keeps it checked.
function applyResubmissionHandbackUnflag(state: State, ctx: ReducerContext): State {
    if (!ctx.isPresentationProject) return state;
    if (!computeHasChanges(state)) return state;
    // Mirror the disabled gate in AdminReviewSection. A non-admin cannot unflag a
    // baseline-flagged submission.
    if (!ctx.isAdmin && state.original.projectSubmissionNeedsAdminReview) return state;
    if (
        state.fields.idVerified === false &&
        state.fields.resubmissionRequired &&
        state.fields.projectSubmissionNeedsAdminReview
    ) {
        return { ...state, fields: { ...state.fields, projectSubmissionNeedsAdminReview: false } };
    }
    return state;
}

function makeReducer(ctx: ReducerContext) {
    return (state: State, action: Action): State => {
        switch (action.type) {
            case 'SCORE_OPTION': {
                // The saved baseline decides which score the select edits, and each save locks it
                // again. The original re-ran syncProjectProgressState on save. Read it from
                // `state.original`, which every RESET updates, not from the initialFields prop. The
                // score then uses the actual-score setter after an in-session Save-and-Approve.
                const setter = gradesProvisionally(state.original, ctx.hasSubmission)
                    ? setProvisionalScoreOptionValue
                    : setScoreOptionValue;
                // A pick of "resubmission_required" can trigger the handback unflag (#17169).
                return applyResubmissionHandbackUnflag(
                    recompute({ ...state, fields: setter(state.fields, action.value) }, ctx),
                    ctx,
                );
            }
            case 'FIELD': {
                const next = { ...state, fields: { ...state.fields, [action.key]: action.value } };
                const recomputed = RECALC_FIELDS.has(action.key) ? recompute(next, ctx) : next;
                // A change to ID Verified can trigger the resubmission-handback unflag (#17169).
                return action.key === 'idVerified' ? applyResubmissionHandbackUnflag(recomputed, ctx) : recomputed;
            }
            case 'ALLOW_RESUBMISSION': {
                // A manual toggle. Set it and manage the due date. Do not recalc, because a recalc
                // would override the toggle.
                const withAllow = { ...state, fields: { ...state.fields, allowResubmission: action.value } };
                return {
                    ...withAllow,
                    fields: applyAllowResubmissionDueDate(
                        withAllow.fields,
                        state.original.resubmissionDueDate,
                        Math.floor(Date.now() / 1000),
                    ),
                };
            }
            case 'AUTOGRADE':
                return recompute(applyAutograde(state, action), ctx);
            case 'CLEAR_PROVISIONAL_GRADE':
                // Ported from the start-success handler in autograding-regenerate-button. Once the
                // server accepts a regrade, null the provisional score and feedback. The grader then
                // cannot see or act on a stale grade during the poll window. AUTOGRADE rewrites them
                // on completion. The recompute call mirrors the original's $watchGroup on
                // provisional_score.
                return recompute(
                    { ...state, fields: { ...state.fields, provisionalScore: null, provisionalFeedback: null } },
                    ctx,
                );
            case 'RESET':
                return { fields: action.fields, original: action.fields };
            default:
                return state;
        }
    };
}

type UseProjectFieldsFormArgs = Pick<
    ProjectFieldsModalProps,
    'initialFields' | 'identity' | 'project' | 'submissionData' | 'onSaved' | 'isAdmin'
>;

// The outcome of a save preparation: the ID-verification gate result, and the fields to persist.
// The fields are null when the gate blocks the save. One example is an approval of a presentation
// project whose ID Verified is unset.
type PreparedSave = { gate: IdVerificationGate; fields: GradingFields | null };

// The save handler and its status. They sit here to keep the main hook small. `prepareSave(approve)`
// returns the gate result and the exact fields to persist, after the approval and auto-flag
// transforms. `onReset` resets the reducer baseline.
function useSave(
    identity: ProjectFieldsModalProps['identity'],
    onSaved: ProjectFieldsModalProps['onSaved'],
    prepareSave: (approve: boolean) => PreparedSave,
    onReset: (fields: GradingFields) => void,
) {
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    const save = useCallback(
        async (approve: boolean) => {
            if (isSaving) return;
            const { gate, fields } = prepareSave(approve);
            if (gate === 'blank' || !fields) {
                // The grader approved a presentation project whose ID Verified is unset. Block the
                // save and prompt them.
                notifyToast(
                    'Please select "ID verified" or "ID NOT verified" before approving the score and feedback.',
                    'danger',
                );
                return;
            }
            setIsSaving(true);
            setSaveError(null);
            try {
                const saved = await saveProjectProgress({ ...identity, fields });
                onReset(fields);
                onSaved(saved);
                if (gate === 'auto_flag') {
                    notifyToast(
                        'Submission flagged for admin review — score and feedback were not sent because ID was not verified.',
                        'info',
                    );
                } else {
                    notifyToast('Project progress saved successfully');
                }
            } catch (error) {
                setSaveError(error instanceof Error ? error.message : 'Failed to save project progress');
            } finally {
                setIsSaving(false);
            }
        },
        [identity, isSaving, onReset, onSaved, prepareSave],
    );

    return { save, isSaving, saveError };
}

// Produces the gate result and the fields to persist. For "Save and Approve" the recompute runs
// first with approve=true, as the original's approve-checkbox watch did. allow_resubmission and the
// due date then match the approval. The provisional values move into the real columns afterwards.
// When the ID-verification gate auto-flags, the save routes to admin review instead (#17035).
function usePrepareSave(state: State, ctx: ReducerContext) {
    return useCallback(
        (approve: boolean): PreparedSave => {
            const gate = idVerificationGate(state.fields, ctx, approve);
            if (gate === 'blank') return { gate, fields: null };
            if (gate === 'auto_flag') return { gate, fields: applyAutoFlagForAdminReview(state.fields) };
            const fields = approve ? applyApproval(recompute(state, ctx, true).fields) : state.fields;
            return { gate, fields };
        },
        [state, ctx],
    );
}

// The field setters bound to dispatch. They memoize once, because dispatch is stable.
function useHandlers(dispatch: React.Dispatch<Action>) {
    return useMemo(
        () => ({
            setScoreOption: (value: ScoreOptionValue) => dispatch({ type: 'SCORE_OPTION', value }),
            setField: <K extends keyof GradingFields>(key: K, value: GradingFields[K]) =>
                dispatch({ type: 'FIELD', key, value }),
            setAllowResubmission: (value: boolean) => dispatch({ type: 'ALLOW_RESUBMISSION', value }),
            applyAutogradeResult: (score: number, feedback: string | null, presentationTranscript: string | null) =>
                dispatch({ type: 'AUTOGRADE', score, feedback, presentationTranscript }),
            clearProvisionalGrade: () => dispatch({ type: 'CLEAR_PROVISIONAL_GRADE' }),
            onReset: (fields: GradingFields) => dispatch({ type: 'RESET', fields }),
        }),
        [dispatch],
    );
}

export function useProjectFieldsForm({
    initialFields,
    identity,
    project,
    submissionData,
    onSaved,
    isAdmin,
}: UseProjectFieldsFormArgs): UseProjectFieldsFormReturn {
    const hasSubmission = !!submissionData.projectSubmission;
    const ctx = useMemo<ReducerContext>(
        () => ({
            passingScore: project.passingScore,
            similarityCheckRequired: project.similarityAndOriginalityCheckRequired,
            historicalSubmissionsCount: submissionData.historicalProjectSubmissions.length,
            isPresentationProject: project.isPresentationProject,
            isAdmin,
            hasSubmission,
        }),
        [project, submissionData.historicalProjectSubmissions.length, isAdmin, hasSubmission],
    );

    const reducer = useMemo(() => makeReducer(ctx), [ctx]);
    const [state, dispatch] = useReducer(reducer, {
        fields: initialFields,
        original: initialFields,
    });

    const { onReset, ...handlers } = useHandlers(dispatch);

    // Re-baseline the reducer when initialFields changes after mount. An admin upload re-seeds the
    // form from the new submission. The first render already holds that state, so skip it.
    const isFirstRender = useRef(true);
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        onReset(initialFields);
    }, [initialFields, onReset]);

    const prepareSave = usePrepareSave(state, ctx);
    const { save, isSaving, saveError } = useSave(identity, onSaved, prepareSave, onReset);

    return {
        fields: state.fields,
        // The saved baseline at load, or at the last save. The "previously approved" banner and the
        // admin-review lock both read it.
        originalFields: state.original,
        // The saved baseline decides this. Every save and reset derives it again from state.original.
        originalIsProvisional: gradesProvisionally(state.original, hasSubmission),
        isProvisionalNow: gradesProvisionally(state.fields, hasSubmission),
        hasSubmission,
        hasChanges: computeHasChanges(state),
        isSaving,
        saveError,
        save,
        ...handlers,
    };
}
